import type { FC, ReactNode } from 'react'
import type { ProjectCategory } from '@/types'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { ProjectCard } from '@/components/widgets/project-card'
import { projectCategories, projects } from '@/constants/projects'

const layout = [
    { span: 'lg:col-span-7', image: 'aspect-[4/3]' },
    { span: 'lg:col-span-5 lg:mt-28', image: 'aspect-[4/3] lg:aspect-[4/5]' },
    { span: 'lg:col-span-5', image: 'aspect-[4/3] lg:aspect-[4/5]' },
    { span: 'lg:col-span-7 lg:mt-28', image: 'aspect-[4/3]' }
]

export const ProjectGrid: FC = (): ReactNode => {
    const [category, setCategory] = useState<ProjectCategory | null>(null)
    const gridRef = useRef<HTMLDivElement>(null)

    const results = category ? projects.filter(project => project.category === category) : projects

    useGSAP(() => {
        const grid = gridRef.current

        if (!grid) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.from(grid.children, {
                y: 50,
                autoAlpha: 0,
                duration: 1.1,
                ease: 'expo.out',
                stagger: 0.09,
                scrollTrigger: { trigger: grid, start: 'top 88%', once: true }
            })
        })

        return () => media.revert()
    }, { scope: gridRef, dependencies: [category], revertOnUpdate: true })

    const filters: { label: string, value: ProjectCategory | null, count: number }[] = [
        { label: "Every project", value: null, count: projects.length },
        ...projectCategories.map(item => ({
            label: item,
            value: item,
            count: projects.filter(project => project.category === item).length
        }))
    ]

    return (
        <div>
            <div className='flex flex-wrap items-center justify-between gap-4'>
                <div role='group' aria-label="Filter projects by type" className='no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0'>
                    {filters.map(filter => {
                        const active = category === filter.value

                        return (
                            <button
                                key={filter.label}
                                type='button'
                                aria-pressed={active}
                                onClick={() => setCategory(filter.value)}
                                className={cn(
                                    'inline-flex h-11 shrink-0 items-center gap-2.5 rounded-full border px-5 text-[14.5px] font-medium transition-colors duration-300',
                                    active ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
                                )}
                            >
                                {filter.label}

                                <span className={cn('font-mono text-[11px]', active ? 'text-cream/55' : 'text-slate')}>
                                    {filter.count}
                                </span>
                            </button>
                        )
                    })}
                </div>

                <p className='font-mono text-[12px] text-slate' aria-live='polite'>
                    {results.length} of {projects.length} projects
                </p>
            </div>

            <div ref={gridRef} className='mt-12 grid gap-x-6 gap-y-14 md:grid-cols-2 lg:grid-cols-12 lg:gap-x-10 lg:gap-y-16'>
                {results.map((project, index) => {
                    const slot = results.length === 1 ? { span: 'md:col-span-2 lg:col-span-12', image: 'aspect-[16/9]' } : layout[index % layout.length]

                    return (
                        <ProjectCard
                            key={project.slug}
                            project={project}
                            className={slot.span}
                            imageClassName={slot.image}
                            sizes={results.length === 1 ? '100vw' : '(min-width: 1024px) 55vw, (min-width: 768px) 50vw, 100vw'}
                        />
                    )
                })}
            </div>
        </div>
    )
}
